import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCalendarCheck, faClock, faUser } from '@fortawesome/free-solid-svg-icons';
import { clinicsService, getAppointmentChiefComplaint, type Appointment, type ClinicRoom } from '../../services/clinicsService';

export default function ClinicsRoomHistoryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [room, setRoom] = useState<ClinicRoom | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    loadData(id);
  }, [id]);

  const loadData = async (roomId: string) => {
    setLoading(true);
    setError(null);
    try {
      // First try to load the room details
      const rooms = await clinicsService.getClinicRooms();
      const currentRoom = rooms.find(r => r.id === roomId);
      if (currentRoom) {
        setRoom(currentRoom);
      }

      // Load appointments for this room
      const appResponse = await clinicsService.getAppointments({ room_id: roomId, per_page: 100 });
      setAppointments(appResponse.appointments || []);
    } catch (err: any) {
      console.error(err);
      setError('Error al cargar el historial de la sala.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs font-medium">Programada</span>;
      case 'in-progress':
        return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-xs font-medium">En progreso</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded-full text-xs font-medium">Completada</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-red-500/20 text-red-300 rounded-full text-xs font-medium">Cancelada</span>;
      case 'no-show':
        return <span className="px-2 py-1 bg-orange-500/20 text-orange-300 rounded-full text-xs font-medium">No asiste</span>;
      default:
        return <span className="px-2 py-1 bg-white/10 text-white/70 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div data-ui="clinics-room-history-loading" className="page-theme-surface page-theme-clinics h-full flex items-center justify-center p-8">
        <div data-ui="clinics-room-history-page-region-001" className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div data-ui="clinics-room-history-page" className="page-theme-surface page-theme-clinics h-full flex flex-col p-1 sm:p-2 w-full animate-fade-in space-y-4">
      {/* Header */}
      <div data-ui="clinics-room-history-page-region-002" className="flex items-center justify-between">
        <div data-ui="clinics-room-history-page-region-003" className="flex items-center gap-3">
          <button
            onClick={() => navigate('/clinics/appointments')}
            className="btn-taratic btn-ghost-dark w-9 h-9 rounded-full flex items-center justify-center p-0 shrink-0"
            title="Volver"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="text-sm" />
          </button>
          <div data-ui="clinics-room-history-page-region-004">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-2.5 flex-wrap">
              Historial de Sala
              {room && (
                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full border border-white/20" style={{ backgroundColor: room.color_code + '40', color: room.color_code }}>
                  {room.name}
                </span>
              )}
            </h1>
            <p className="text-white/50 text-xs sm:text-sm mt-0.5">Histórico de citas agendadas en esta sala</p>
          </div>
        </div>
      </div>

      {error && (
        <div data-ui="clinics-room-history-page-region-005" className="p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-200 text-xs sm:text-sm">
          {error}
        </div>
      )}

      {/* List */}
      <div data-ui="clinics-room-history-page-region-006" className="bg-[#0d1117]/80 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
        <div data-ui="clinics-room-history-page-region-007" className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm text-left">
            <thead className="text-[11px] sm:text-xs text-white/60 bg-black/20 uppercase border-b border-white/10">
              <tr>
                <th className="px-4 py-2.5 font-medium">Fecha y Hora</th>
                <th className="px-4 py-2.5 font-medium">Paciente</th>
                <th className="px-4 py-2.5 font-medium">Doctor</th>
                <th className="px-4 py-2.5 font-medium">Estado</th>
                <th className="px-4 py-2.5 font-medium">Motivo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {appointments.map((app) => (
                <tr key={app.id} className="hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div data-ui="clinics-room-history-page-region-008" className="flex flex-col">
                      <span className="text-white font-medium capitalize text-xs sm:text-sm">
                        {format(parseISO(app.start_time), 'EEEE, d MMMM yyyy', { locale: es })}
                      </span>
                      <span className="text-white/50 text-[11px] flex items-center gap-1 mt-0.5">
                        <FontAwesomeIcon icon={faClock} />
                        {format(parseISO(app.start_time), 'HH:mm')} - {format(parseISO(app.end_time), 'HH:mm')}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div data-ui="clinics-room-history-page-region-009" className="flex items-center gap-2">
                      <div data-ui="clinics-room-history-page-region-010" className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 shrink-0">
                        <FontAwesomeIcon icon={faUser} className="text-[10px]" />
                      </div>
                      <span className="text-white/90">{app.patient_name || 'Desconocido'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white/80 whitespace-nowrap">{app.doctor_name || 'No asignado'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {getStatusBadge(app.status)}
                  </td>
                  <td className="px-4 py-3 text-white/70 max-w-[200px] truncate" title={getAppointmentChiefComplaint(app) || ''}>
                    {getAppointmentChiefComplaint(app) || '-'}
                  </td>
                </tr>
              ))}
              {appointments.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-white/50">
                    <FontAwesomeIcon icon={faCalendarCheck} className="text-3xl mb-2 opacity-50 block mx-auto" />
                    No hay citas registradas en esta sala.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
